/* ════════════ THE CIRCUIT ════════════
   The tor page's atmosphere: relay nodes scattered in the dark,
   circuits lighting up three hops at a time while a packet rides
   them, shedding one envelope of color per hop. Plus the hero's
   onion diagram: three concentric encryption rings, breathing. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

const PURPLE = "#b07cff";
const GREEN = "#abcd03";
const DIM = "#5a4a7a";

/* ───────── background relay mesh ───────── */
function circuit(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#circuit")!;
  const ctx = c.getContext("2d")!;

  type Node = { x: number; y: number; r: number };
  type Hop = {
    path: [Node, Node, Node, Node]; // you → guard → middle → exit
    t: number; // 0..3, position along the three legs
    speed: number;
    fade: number; // 1 → 0 after arrival
  };
  let nodes: Node[] = [];
  let hops: Hop[] = [];

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(42, Math.floor((c.width * c.height) / 42000));
    nodes = Array.from({ length: count }, () => ({
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      r: 1.5 + Math.random() * 1.8,
    }));
    hops = [];
  };
  seed();
  addEventListener("resize", seed, { signal });

  const pick = (): Node => nodes[(Math.random() * nodes.length) | 0]!;
  const newHop = (): Hop => ({
    path: [pick(), pick(), pick(), pick()],
    t: 0,
    speed: 0.004 + Math.random() * 0.005,
    fade: 1,
  });

  const lerp = (a: Node, b: Node, p: number) => ({
    x: a.x + (b.x - a.x) * p,
    y: a.y + (b.y - a.y) * p,
  });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);

    // the resting mesh: every relay is just a stranger in the crowd
    ctx.fillStyle = DIM;
    for (const n of nodes) {
      ctx.globalAlpha = 0.4;
      ctx.fillRect(n.x - n.r / 2, n.y - n.r / 2, n.r, n.r);
    }

    for (const h of hops) {
      const leg = Math.min(2, Math.floor(h.t));
      const alpha = h.t >= 3 ? h.fade : 1;

      // lit legs of the circuit, already-traveled ones at full length
      for (let i = 0; i < 3; i++) {
        if (i > leg) break;
        const a = h.path[i]!;
        const b = h.path[i + 1]!;
        const p = i < leg ? 1 : h.t - leg;
        const end = lerp(a, b, p);
        ctx.globalAlpha = 0.16 * alpha;
        ctx.strokeStyle = PURPLE;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
      }

      // the packet: purple while wrapped, green once the last layer peels
      if (h.t < 3) {
        const a = h.path[leg]!;
        const b = h.path[leg + 1]!;
        const pos = lerp(a, b, h.t - leg);
        // 3 → 1 envelopes left as the hops peel
        const layers = 3 - leg;
        ctx.globalAlpha = 0.85;
        ctx.fillStyle = leg === 2 ? GREEN : PURPLE;
        const s = 2 + layers * 1.2;
        ctx.fillRect(pos.x - s / 2, pos.y - s / 2, s, s);
      }

      // relay nodes on the active path glow
      for (let i = 0; i <= Math.min(3, leg + 1); i++) {
        const n = h.path[i]!;
        ctx.globalAlpha = 0.7 * alpha;
        ctx.fillStyle = i === 3 ? GREEN : PURPLE;
        ctx.fillRect(n.x - 2, n.y - 2, 4, 4);
      }
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    // a single frozen, fully-lit circuit
    hops = [newHop()];
    hops[0]!.t = 2.5;
    draw();
    return;
  }

  const tick = () => {
    if (signal.aborted) return;
    if (hops.length < 4 && Math.random() < 0.012) hops.push(newHop());
    for (const h of hops) {
      if (h.t < 3) h.t += h.speed * 60;
      else h.fade -= 0.01;
    }
    hops = hops.filter((h) => h.fade > 0);
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

/* ───────── hero onion: three rings, one secret ───────── */
function onion(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#onion")!;
  const ctx = c.getContext("2d")!;
  const W = c.width;
  const H = c.height;
  const cx = W / 2;
  const cy = H / 2;

  const draw = (t: number) => {
    ctx.clearRect(0, 0, W, H);
    const rings = [
      { r: 86, label: "GUARD", alpha: 0.9 },
      { r: 60, label: "MIDDLE", alpha: 0.7 },
      { r: 34, label: "EXIT", alpha: 0.5 },
    ];
    rings.forEach((ring, i) => {
      const wob = REDUCED ? 0 : Math.sin(t / 900 + i * 1.4) * 2.5;
      ctx.beginPath();
      ctx.arc(cx, cy, ring.r + wob, 0, Math.PI * 2);
      ctx.strokeStyle = PURPLE;
      ctx.globalAlpha = ring.alpha * 0.55;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 5]);
      ctx.lineDashOffset = REDUCED ? 0 : -t / (40 + i * 25);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = ring.alpha * 0.8;
      ctx.fillStyle = PURPLE;
      ctx.font = '8px "IBM Plex Mono", monospace';
      ctx.textAlign = "center";
      ctx.fillText(ring.label, cx, cy - ring.r - wob - 4);
    });
    // the payload nobody can attribute
    const pulse = REDUCED ? 0.9 : 0.7 + Math.sin(t / 500) * 0.3;
    ctx.globalAlpha = pulse;
    ctx.fillStyle = GREEN;
    ctx.fillRect(cx - 4, cy - 4, 8, 8);
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw(0);
    return;
  }
  const tick = (t: number) => {
    if (signal.aborted) return;
    draw(t);
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

onPage((signal) => {
  circuit(signal);
  onion(signal);
});
